import React, { useEffect, useState } from 'react'
import { useConfigurationStore, useCountdownFSBonusStore } from '@stores'
import { formatMoney } from '@utils/money'
import { Bonus } from 'game-ui-types'
import { getCountdownDisplayItems, getCountdownValuesFromBonus } from '@utils/bonus'
import spritesheetSrc from '@assets/bonus-system/countdown-gift-animation.png'
import animatedSpriteConfig from '@assets/bonus-system/countdown-gift-animation.json'
import { useTranslation } from '@amusnet/translations/react'
import { CountdownCounterDisplayItems, CountdownCounterValues } from '../types'
import { AnimatedSprite } from '@amusnet/react-ui'

interface CFSSingleBonusProps {
    updateSelectedBonus: (bonus: Bonus) => void
}

const CFSSingleBonus: React.FC<CFSSingleBonusProps> = ({ updateSelectedBonus }: CFSSingleBonusProps) => {
    const bonuses = useCountdownFSBonusStore((state) => state.bonuses)

    const { t } = useTranslation()
    const currencyHideCents = useConfigurationStore((state) => state.currencyHideCents)
    const currency = useConfigurationStore((state) => state.currency)

    const [betValue, setBetValue] = useState('')
    const [countdown, setCountdown] = useState<CountdownCounterValues>(null)

    useEffect(() => {
        if (bonuses.length) {
            updateSelectedBonus(bonuses[0])
            setBetValue(formatMoney(Number(bonuses[0].betValue), currencyHideCents))
            setCountdown(getCountdownValuesFromBonus(bonuses[0], true))
        }
    }, [bonuses])

    const renderCounter = () => {
        if (!countdown) return null

        const displayItems: CountdownCounterDisplayItems = [
            { val: countdown.days, label: t('gameui.bonusSystem.timeLeftDays') },
            { val: countdown.hours, label: t('gameui.bonusSystem.timeLeftHours') },
            { val: countdown.minutes, label: t('gameui.bonusSystem.timeLeftMinutes') },
            { val: countdown.seconds, label: t('gameui.bonusSystem.timeLeftSeconds') },
        ]

        const { firstStringDigits, firstStringLabel, secondStringDigits, secondStringLabel } =
            getCountdownDisplayItems(displayItems)

        return (
            <p className="rounded-[30px] font-bold bg-[#1E1B1B] py-[8px] px-[25px]">
                <span className="text-4xl text-[#D9D9D9] leading-snug">{`${t('gameui.bonusSystem.forText')}: `}</span>
                <span className="text-4xl font-black text-yellow-400 leading-snug">
                    <span className="font-black">{firstStringDigits}</span>
                    <span>{firstStringLabel}</span>
                    <span className="font-black">{secondStringDigits}</span>
                    <span>{secondStringLabel}</span>
                </span>
            </p>
        )
    }

    return (
        <div className="flex flex-col items-center justify-between">
            <div className="flex flex-col w-full bg-[#19191C]">
                <AnimatedSprite
                    spritesheetSrc={spritesheetSrc}
                    config={animatedSpriteConfig}
                    duration={1.5}
                    className="m-auto"
                />

                <div
                    id="countdown-freespin-bonus-text"
                    className="flex flex-col items-center w-full text-center pb-4 bg-[linear-gradient(180deg,#19191C_19.44%,#793F06_100%)]"
                >
                    <p className="text-4xl font-bold text-white">{t('gameui.congratulations')}</p>
                    <p className="text-4xl font-bold text-white my-[10px]">{t('gameui.bonusSystem.youGet')}</p>
                    <p className="text-4xl font-bold text-yellow-400 leading-tight px-1">
                        {t('gameui.bonusSystem.unlimitedFreeSpinsTitle')}
                    </p>
                    <p className="text-4xl font-black text-white my-[10px]">
                        x {betValue} <span className="text-[24px] font-normal">{currency}</span>
                    </p>
                    {renderCounter()}
                </div>
            </div>
        </div>
    )
}

export default CFSSingleBonus
